import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchCartRepo,
  addToCartRepo,
  removeFromCartRepo,
  clearCartRepo,
} from "../repositories/cart.repository";
import { useMemo } from "react";
import { CartItem } from "../types/common";
import { addToCart } from "../api/cartApi";
import { useOrderStore } from "../store/useOrderStore";

export const useCart = (customerId: string) => {
  const queryClient = useQueryClient();
  const setActiveOrderId = useOrderStore((s) => s.setActiveOrderId);

  // ---- Fetch raw cart items ----
  const {
    data: rawItems,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["cart", customerId],
    queryFn: () => fetchCartRepo(customerId),
    enabled: !!customerId,
  });

  // ---- Group items by packageId and count quantity ----
  const cartItems: CartItem[] = useMemo(() => {
    if (!rawItems) return [];

    const grouped: Record<string, CartItem> = {};

    for (const item of rawItems) {
      const key = item.packageId;

      if (!grouped[key]) {
        grouped[key] = {
          ...item,
        };
      } else {
        grouped[key].quantity! += 1;
      }
    }

    return Object.values(grouped);
  }, [rawItems]);

  // ---- Mutations ----
  const addToCartMutation = useMutation({
    mutationFn: (items: CartItem[]) => addToCartRepo(customerId, items),
    onSuccess: (orderId) => {
      queryClient.invalidateQueries({ queryKey: ["cart", customerId] });
      setActiveOrderId(orderId);
    },
  });

  const removeFromCartMutation = useMutation({
    mutationFn: (itemId: string) => removeFromCartRepo(customerId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart", customerId] });
    },
  });

  const clearCartMutation = useMutation({
    mutationFn: () => clearCartRepo(customerId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cart", customerId] });
    },
  });

  // ---- Quantity Helpers ----
  const increaseQuantity = (itemId: string) => {
    const item = cartItems.find((i) => i.packageId === itemId);
    if (item) {
      addToCartMutation.mutate([{ ...item, quantity: 1 }]);
    }
  };

  const decreaseQuantity = (itemId: string) => {
    const item = cartItems.find((i) => i.packageId === itemId);
    if (!item) return;

    if ((item.quantity ?? 1) > 1) {
      addToCartMutation.mutate([{ ...item, quantity: -1 }]);
    } else {
      removeFromCartMutation.mutate(itemId);
    }
  };

  // ---- Totals ----
  const getTotalQuantity = () =>
    cartItems.reduce((sum, i) => sum + (i.quantity ?? 1), 0);

  const getTotalPrice = () =>
    cartItems.reduce((sum, i) => sum + Number(i.price) * (i.quantity ?? 1), 0);

  const getQuantityById = (id: string) =>
    cartItems.find((i) => i.packageId === id)?.quantity ?? 0;

  // ---- Return interface ----
  return {
    rawItems: rawItems,
    cartItems,
    isLoading,
    error,
    refetch,
    addToCart: addToCartMutation.mutate,
    addToCartAsync: addToCartMutation.mutateAsync,
    addToCartLoading: addToCartMutation.isPending,
    removeFromCart: removeFromCartMutation.mutate,
    clearCart: clearCartMutation.mutate,
    clearCartAsync: clearCartMutation.mutateAsync,
    clearCartLoading: clearCartMutation.isPending,
    increaseQuantity,
    decreaseQuantity,
    getTotalQuantity,
    getTotalPrice,
    getQuantityById,
  };
};
